const JPEG_FILE_TYPE = 'image/jpeg';

// JPEGs from phone cameras often store their rotation in EXIF instead of rotating
// the pixel data. The avatar service re-processes uploads, which drops that metadata.
// Normalising here means the uploaded pixels are always in their display orientation.
export const getExifOrientation = (data: ArrayBuffer): number => {
  const view = new DataView(data);

  if (view.byteLength < 4 || view.getUint16(0, false) !== 0xffd8) {
    return 1;
  }

  let offset = 2;
  while (offset + 4 <= view.byteLength) {
    const marker = view.getUint16(offset, false);
    offset += 2;

    if ((marker & 0xff00) !== 0xff00) break;
    if (marker === 0xffda || marker === 0xffd9) break;

    const length = view.getUint16(offset, false);
    if (length < 2 || offset + length > view.byteLength) break;
    const segmentEnd = offset + length;

    if (
      marker === 0xffe1 &&
      length >= 16 &&
      view.getUint32(offset + 2, false) === 0x45786966
    ) {
      const tiffOffset = offset + 8;
      const littleEndian = view.getUint16(tiffOffset, false) === 0x4949;
      const firstDirectoryOffset = view.getUint32(tiffOffset + 4, littleEndian);
      const directoryOffset = tiffOffset + firstDirectoryOffset;

      if (directoryOffset + 2 > segmentEnd) return 1;

      const entries = view.getUint16(directoryOffset, littleEndian);
      for (let index = 0; index < entries; index += 1) {
        const entryOffset = directoryOffset + 2 + index * 12;
        if (entryOffset + 12 > segmentEnd) return 1;
        if (view.getUint16(entryOffset, littleEndian) === 0x0112) {
          return view.getUint16(entryOffset + 8, littleEndian);
        }
      }
    }

    offset += length;
  }

  return 1;
};

const isJpeg = (file: File) =>
  file.type === JPEG_FILE_TYPE || /\.jpe?g$/i.test(file.name);

const readOrientation = async (file: File): Promise<number> => {
  if (!isJpeg(file) || !file.arrayBuffer) return 1;
  return getExifOrientation(await file.arrayBuffer());
};

const canvasToFile = (canvas: HTMLCanvasElement, originalFile: File) =>
  new Promise<File>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Unable to create an oriented image file'));
          return;
        }
        resolve(
          new File([blob], originalFile.name, {
            type: JPEG_FILE_TYPE,
            lastModified: originalFile.lastModified,
          })
        );
      },
      JPEG_FILE_TYPE,
      0.92
    );
  });

const loadImage = (file: File) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Unable to decode image'));
    };
    image.src = objectUrl;
  });

const drawWithOrientation = (
  canvas: HTMLCanvasElement,
  image: CanvasImageSource,
  width: number,
  height: number,
  orientation: number
) => {
  const isSideways = orientation >= 5 && orientation <= 8;
  canvas.width = isSideways ? height : width;
  canvas.height = isSideways ? width : height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Unable to create image canvas');

  switch (orientation) {
    case 2:
      context.translate(width, 0);
      context.scale(-1, 1);
      break;
    case 3:
      context.translate(width, height);
      context.rotate(Math.PI);
      break;
    case 4:
      context.translate(0, height);
      context.scale(1, -1);
      break;
    case 5:
      context.rotate(Math.PI / 2);
      context.scale(1, -1);
      break;
    case 6:
      context.rotate(Math.PI / 2);
      context.translate(0, -height);
      break;
    case 7:
      context.rotate(-Math.PI / 2);
      context.scale(1, -1);
      break;
    case 8:
      context.rotate(-Math.PI / 2);
      context.translate(-width, 0);
      break;
    default:
      break;
  }
  context.drawImage(image, 0, 0, width, height);
};

/**
 * Returns a JPEG whose pixels have the same orientation as the camera preview.
 * The original is returned when it is not a rotated JPEG or when a browser cannot
 * decode it (for example, an unsupported HEIC image).
 */
const normalizeImageOrientation = async (file: File): Promise<File> => {
  const orientation = await readOrientation(file);
  if (orientation === 1 || orientation < 2 || orientation > 8) return file;

  try {
    const canvas = document.createElement('canvas');

    if (typeof createImageBitmap === 'function') {
      let bitmap: ImageBitmap | null = null;
      try {
        bitmap = await createImageBitmap(file, {
          // The DOM typings bundled with this project predate `from-image`.
          imageOrientation: 'from-image' as ImageOrientation,
        });
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;
        const context = canvas.getContext('2d');
        if (!context) throw new Error('Unable to create image canvas');
        context.drawImage(bitmap, 0, 0);
        return await canvasToFile(canvas, file);
      } catch (_) {
        // Fall through for browsers without ImageBitmap EXIF-orientation support.
      } finally {
        if (bitmap) bitmap.close();
      }
    }

    const image = await loadImage(file);
    drawWithOrientation(
      canvas,
      image,
      image.naturalWidth,
      image.naturalHeight,
      orientation
    );
    return await canvasToFile(canvas, file);
  } catch (_) {
    // Uploading the original is preferable to rejecting a photo on older browsers.
    return file;
  }
};

export default normalizeImageOrientation;
